'use client';

import {
  LazyMotion,
  domAnimation,
  m,
  MotionConfigContext,
  useInView,
  useReducedMotion,
} from 'motion/react';
import { useContext, useEffect, useRef, useState } from 'react';
import type { FloatingVoiceDockStatus } from '@/lib/floating-voice-dock-state';
import { VoiceCompanionFace } from './voice-companion-face';
import { getVoiceColor, voiceColorCss } from './voice-companion-colors';

export function VoiceCompanionAvatar({
  status,
  inputMode,
  micMuted,
  pttActive,
}: {
  status: FloatingVoiceDockStatus;
  inputMode: 'vad' | 'ptt';
  micMuted: boolean;
  pttActive: boolean;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { amount: 0.5 });
  const reducedMotion = useReducedMotion();
  const motionConfig = useContext(MotionConfigContext);
  const [tabVisible, setTabVisible] = useState(true);

  useEffect(() => {
    const update = () => setTabVisible(document.visibilityState === 'visible');
    update();
    document.addEventListener('visibilitychange', update);
    return () => document.removeEventListener('visibilitychange', update);
  }, []);

  const speaking = status === 'speaking';
  const moving =
    inView &&
    tabVisible &&
    !reducedMotion &&
    motionConfig.reducedMotion !== 'always';
  const listening = status === 'listening';
  const mode = speaking ? 'speaking' : listening ? 'listening' : 'idle';
  const voiceState = micMuted
    ? 'muted'
    : inputMode === 'ptt' && !pttActive && status === 'listening'
      ? 'waiting'
      : status;

  return (
    <LazyMotion features={domAnimation}>
      <span
        ref={ref}
        data-slot="voice-companion-face"
        data-voice-state={voiceState}
        className="relative flex size-16 items-center justify-center"
        style={{ color: voiceColorCss(getVoiceColor(mode, 0)) }}
        aria-hidden="true"
      >
        <m.span
          className="absolute inset-1 rounded-full border border-current"
          animate={
            moving && speaking
              ? { scale: [0.9, 1.18, 0.9], opacity: [0.2, 0.65, 0.2] }
              : { scale: 1, opacity: 0 }
          }
          transition={
            moving && speaking
              ? { duration: 1.15, repeat: Infinity, ease: 'easeInOut' }
              : { duration: 0.15 }
          }
        />
        <m.span
          className="relative flex size-16 items-center justify-center drop-shadow-sm"
          animate={
            moving && speaking
              ? { scale: [1, 1.08, 0.97, 1.06, 1] }
              : { scale: 1 }
          }
          transition={
            moving && speaking
              ? { duration: 0.85, repeat: Infinity, ease: 'easeInOut' }
              : { duration: 0.15 }
          }
        >
          <VoiceCompanionFace mode={mode} running={moving} />
        </m.span>
        {speaking && (
          <m.svg
            viewBox="0 0 52 52"
            className="pointer-events-none absolute inset-0 size-16 overflow-visible"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            aria-hidden="true"
            animate={
              moving
                ? { opacity: [0.25, 0.8, 0.25], scale: [0.96, 1.08, 0.96] }
                : { opacity: 0.7, scale: 1 }
            }
            transition={
              moving
                ? { duration: 0.85, repeat: Infinity, ease: 'easeInOut' }
                : { duration: 0 }
            }
          >
            <path d="M4 18c-2 4-2 12 0 16M48 18c2 4 2 12 0 16" />
          </m.svg>
        )}
      </span>
    </LazyMotion>
  );
}
